import React from "react";
import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import PageShell from "../components/PageShell";
import { Section } from "../components/MarketingPrimitives";

const TermsPage = () => {
  return (
    <PageShell>
      <PageMeta
        title="Terms and Conditions | BestSalesCoach.ai"
        description="Terms and Conditions for BestSalesCoach.ai, a Victoria, Australia-based AI GTM Execution Intelligence platform."
        canonicalPath="/terms"
      />

      <Section
        id="terms-hero"
        eyebrow="Legal"
        title="Terms and Conditions"
        description="These terms govern your access to and use of the BestSalesCoach.ai website, demo request forms, content, communications, and related services."
        tone="hero"
      >
        <div className="marketing-card legal-card">
          <p className="marketing-body-copy">
            <strong>Effective date:</strong> April 29, 2026
          </p>
        </div>
      </Section>

      <Section id="terms-content" tone="light" className="legal-section">
        <article className="marketing-card legal-card legal-prose">
          <p>Welcome to BestSalesCoach.ai.</p>
          <p>
            These Terms and Conditions govern your access to and use of the BestSalesCoach.ai website,
            demo request forms, content, communications, and any related services made available
            through this website.
          </p>
          <p>
            BestSalesCoach.ai is operated from Victoria, Australia by BestSalesCoach.ai
            ("BestSalesCoach.ai", "we", "us", or "our").
          </p>
          <p>
            By accessing this website, submitting a demo request, or using our services, you agree to
            these Terms and Conditions.
          </p>
          <p>
            If you do not agree with these Terms, please do not use this website or submit your
            details.
          </p>

          <h3>1. About BestSalesCoach.ai</h3>
          <p>
            BestSalesCoach.ai provides AI-assisted GTM execution intelligence, sales execution
            guidance, pipeline review support, account strategy analysis, stakeholder analysis,
            MEDDICC-related insights, and related GTM Engineering services.
          </p>
          <p>
            The information on this website is provided for general business information and
            demonstration purposes only. It does not guarantee any specific revenue, pipeline, sales,
            conversion, or business outcome.
          </p>

          <h3>2. Demo requests and contact forms</h3>
          <p>
            When you submit a “Book a 20-Minute Demo” request, we may collect information such as your:
          </p>
          <ul className="marketing-bullet-list">
            <li>name;</li>
            <li>email address;</li>
            <li>phone number, if provided;</li>
            <li>company name;</li>
            <li>role or title;</li>
            <li>company website;</li>
            <li>buyer type or area of interest;</li>
            <li>message or enquiry details; and</li>
            <li>website source, referral, or campaign information.</li>
          </ul>
          <p>We use this information to:</p>
          <ul className="marketing-bullet-list">
            <li>respond to your enquiry;</li>
            <li>arrange or follow up on a product or services demo;</li>
            <li>understand your GTM or revenue execution needs;</li>
            <li>manage the enquiry in our CRM;</li>
            <li>send relevant information connected to your request; and</li>
            <li>improve our website, services, and customer experience.</li>
          </ul>
          <p>
            Submitting a demo request does not create a client relationship, service agreement,
            partnership, employment relationship, or obligation for either party to proceed.
          </p>
          <p>
            Please also read our{" "}
            <Link className="marketing-text-link" to="/privacy">
              Privacy Policy
            </Link>
            , which explains how we collect, use, store, and disclose personal information.
          </p>

          <h3>3. Use of GoModern CRM and third-party service providers</h3>
          <p>
            We may use third-party service providers, including GoModern CRM, to receive, store,
            process, and manage demo requests, website enquiries, contact information, email
            notifications, scheduling, and follow-up communications.
          </p>
          <p>
            When you submit a demo request, your information may be sent to our CRM and may be
            forwarded to our nominated contact email, currently:
          </p>
          <p>
            <strong>admin@bestsalescoach.ai</strong>
          </p>
          <p>
            We require our service providers to handle information only for the purposes of supporting
            our business operations, communications, CRM workflows, and related services.
          </p>

          <h3>4. Privacy and personal information</h3>
          <p>
            We collect and handle personal information in accordance with applicable Australian privacy
            obligations.
          </p>
          <p>
            By submitting a demo request, you consent to us collecting, storing, and using your
            information for the purposes described in these Terms and our{" "}
            <Link className="marketing-text-link" to="/privacy">
              Privacy Policy
            </Link>
            .
          </p>
          <p>
            You may contact us to request access to, correction of, or deletion of your personal
            information, subject to any legal or business record-keeping obligations.
          </p>
          <p>
            <strong>Contact email:</strong>
          </p>
          <p>
            <strong>admin@bestsalescoach.ai</strong>
          </p>
          <p>
            We will take reasonable steps to protect personal information from misuse, interference,
            loss, unauthorised access, modification, or disclosure.
          </p>

          <h3>5. Email communications and marketing</h3>
          <p>
            If you submit a demo request, we may contact you about that request using the contact
            details you provide.
          </p>
          <p>
            We may also send you related information about BestSalesCoach.ai, GTM Engineering
            services, product updates, or relevant business content where permitted by law or where you
            have provided consent.
          </p>
          <p>
            You may unsubscribe from marketing communications at any time by using the unsubscribe
            option provided in the message or by contacting us at:
          </p>
          <p>
            <strong>admin@bestsalescoach.ai</strong>
          </p>
          <p>
            We do not intend to send unsolicited commercial electronic messages in breach of
            Australian spam laws.
          </p>

          <h3>6. Website content</h3>
          <p>
            All website content, including text, graphics, product descriptions, screenshots, layouts,
            frameworks, concepts, and branding, is provided for general information only.
          </p>
          <p>We may update, change, remove, or replace website content at any time without notice.</p>
          <p>
            While we aim to provide accurate and useful information, we do not warrant that the
            website content is complete, current, error-free, or suitable for your specific business
            requirements.
          </p>

          <h3>7. AI-generated insights and business decisions</h3>
          <p>
            BestSalesCoach.ai may use artificial intelligence to assist with GTM execution, account
            strategy, pipeline review, MEDDICC-related analysis, stakeholder analysis, coaching
            prompts, and suggested next actions.
          </p>
          <p>
            AI-generated outputs are intended to support human judgement. They should not be treated
            as the sole basis for business, sales, employment, legal, financial, or strategic
            decisions.
          </p>
          <p>
            You remain responsible for reviewing, validating, and deciding whether to use any output,
            recommendation, insight, or suggested action.
          </p>

          <h3>8. Acceptable use</h3>
          <p>You must not use this website or any related service to:</p>
          <ul className="marketing-bullet-list">
            <li>submit false, misleading, unlawful, or fraudulent information;</li>
            <li>interfere with the operation, security, or availability of the website;</li>
            <li>attempt to gain unauthorised access to systems, data, or accounts;</li>
            <li>copy, scrape, reverse engineer, or misuse website content or platform functionality;</li>
            <li>upload or transmit malicious code;</li>
            <li>infringe intellectual property rights;</li>
            <li>breach privacy, confidentiality, or data protection obligations; or</li>
            <li>use the website or services for any unlawful purpose.</li>
          </ul>
          <p>
            We may restrict access to the website or services if we reasonably believe these Terms
            have been breached.
          </p>

          <h3>9. Intellectual property</h3>
          <p>
            Unless otherwise stated, all intellectual property rights in this website,
            BestSalesCoach.ai content, product concepts, page design, frameworks, software, text,
            graphics, branding, and related materials are owned by or licensed to us.
          </p>
          <p>You may view and use the website for your own internal business evaluation purposes.</p>
          <p>
            You must not copy, reproduce, distribute, modify, commercialise, or create derivative
            works from our content without our prior written permission.
          </p>

          <h3>10. Third-party links and tools</h3>
          <p>
            This website may link to or integrate with third-party websites, tools, forms, calendars,
            CRMs, or services.
          </p>
          <p>
            We are not responsible for the content, availability, security, privacy practices, or
            performance of third-party services.
          </p>
          <p>
            Your use of any third-party service may be governed by that third party’s own terms and
            privacy policy.
          </p>

          <h3>11. Service availability</h3>
          <p>
            We aim to keep the website available and functioning, but we do not guarantee
            uninterrupted, secure, or error-free access.
          </p>
          <p>
            We may suspend, update, modify, or discontinue any part of the website or services at any
            time.
          </p>

          <h3>12. Limitation of liability</h3>
          <p>
            To the maximum extent permitted by law, we exclude liability for any loss, damage, cost,
            or expense arising from your use of, or inability to use, this website, demo request
            forms, content, communications, or related services.
          </p>
          <p>
            This includes indirect, incidental, special, consequential, or business losses, including
            loss of revenue, loss of profits, loss of opportunity, loss of data, or business
            interruption.
          </p>
          <p>
            Nothing in these Terms excludes, restricts, or modifies any rights, guarantees, remedies,
            or protections that cannot be excluded under applicable law, including the Australian
            Consumer Law.
          </p>

          <h3>13. No professional advice</h3>
          <p>
            The website content and any demo-related discussion are provided for general business
            information only.
          </p>
          <p>
            They do not constitute legal, financial, accounting, employment, tax, or professional
            advice.
          </p>
          <p>
            You should obtain independent advice before making decisions based on information provided
            through this website or any demo.
          </p>

          <h3>14. Changes to these Terms</h3>
          <p>We may update these Terms and Conditions from time to time.</p>
          <p>The updated version will be posted on this page with a revised effective date.</p>
          <p>
            Your continued use of the website after changes are published means you accept the updated
            Terms.
          </p>

          <h3>15. Governing law</h3>
          <p>These Terms are governed by the laws of Victoria, Australia.</p>
          <p>
            The parties submit to the non-exclusive jurisdiction of the courts of Victoria, Australia
            and any courts entitled to hear appeals from those courts.
          </p>

          <h3>16. Contact us</h3>
          <p>For questions about these Terms, demo requests, privacy, or your personal information, contact:</p>
          <p>
            BestSalesCoach.ai
            <br />
            Email: admin@bestsalescoach.ai
            <br />
            Location: Victoria, Australia
          </p>
        </article>
      </Section>
    </PageShell>
  );
};

export default TermsPage;
